import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * PWA update watcher.
 * - Polls the service worker for new versions every 30 min (+ on tab focus).
 * - When a new SW is installed AND an existing controller is present (i.e. it's an update,
 *   not the first install), shows a persistent toast with "Osveži" button.
 * - Clicking the button posts `SKIP_WAITING` to the waiting SW; when the new SW takes
 *   control (`controllerchange`), the page reloads automatically.
 */
const CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 min

const PWAUpdateWatcher = () => {
  const shownRef = useRef(false);
  const reloadingRef = useRef(false);

  useEffect(() => {
    // Guard: skip in sandboxed iframes / non-SW browsers / dev environment without SW
    if (typeof window === "undefined") return;
    if (window.self !== window.top) return;
    if (!("serviceWorker" in navigator)) return;

    let intervalId;

    const promptReload = (reg) => {
      if (shownRef.current) return;
      shownRef.current = true;
      toast.info("Nova različica aplikacije je na voljo", {
        description: "Osvežite za najnovejše popravke in funkcije.",
        duration: Infinity,
        action: {
          label: "Osveži",
          onClick: () => {
            const worker = reg.waiting || reg.installing;
            if (worker) {
              worker.postMessage({ type: "SKIP_WAITING" });
            } else {
              // Fallback if the waiting worker vanished
              window.location.reload();
            }
          },
        },
      });
    };

    const attachUpdateListener = (reg) => {
      // If a worker is already waiting from a previous session, prompt immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        promptReload(reg);
      }
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            // A previous SW is controlling the page → this is an UPDATE.
            promptReload(reg);
          }
        });
      });
    };

    // Reload the page once a new SW takes over control.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    });

    navigator.serviceWorker.getRegistration("/sw.js").then((reg) => {
      if (!reg) return;
      attachUpdateListener(reg);

      // Periodic check
      const check = () => {
        try { reg.update(); } catch (_) { /* ignore */ }
      };
      intervalId = setInterval(check, CHECK_INTERVAL_MS);
      // Also check when the tab regains focus (users often background PWAs for days)
      const onFocus = () => {
        if (document.visibilityState === "visible") check();
      };
      document.addEventListener("visibilitychange", onFocus);
      // Cleanup listener stored in effect return
      window.__dpPwaFocusHandler = onFocus;
    }).catch(() => { /* ignore */ });

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (window.__dpPwaFocusHandler) {
        document.removeEventListener("visibilitychange", window.__dpPwaFocusHandler);
        delete window.__dpPwaFocusHandler;
      }
    };
  }, []);

  return null;
};

export default PWAUpdateWatcher;
